import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, Puzzle, Star } from 'lucide-react';
import { useExtensions } from '@/hooks/use-content';
import type { ExtensionRow as ExtensionItem } from '@/types/database';
import SectionEyebrow from './SectionEyebrow';

const ExtensionCardSkeleton = () => (
  <Card className="p-6 flex flex-col gap-4">
    <div className="flex items-start gap-4">
      <Skeleton className="h-11 w-11 rounded-md shrink-0" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <Skeleton className="h-4 w-1/3 mt-auto" />
  </Card>
);

const ExtensionCard = memo(({ extension }: { extension: ExtensionItem }) => {
  const { t } = useTranslation();
  return (
    <Card className="p-6 flex flex-col gap-4 hover:shadow-soft hover:-translate-y-1 transition-all duration-300">
      <div className="flex items-start gap-4">
        <div className="shrink-0 rounded-md bg-primary/10 p-3 text-primary">
          <Puzzle className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold truncate">{extension.name}</h3>
          {extension.review_count > 0 ? (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <div className="flex text-primary">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-3.5 w-3.5 ${i < extension.rating ? 'fill-current' : ''}`} />
                ))}
              </div>
              <span>{t('extensions.reviews', { count: extension.review_count })}</span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">{t('extensions.notRated')}</span>
          )}
        </div>
      </div>

      <p className="text-muted-foreground text-sm leading-relaxed">{extension.description}</p>

      <a
        href={extension.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-accent transition-colors mt-auto"
        aria-label={t('extensions.viewOnFirefoxAria', { name: extension.name })}
      >
        {t('extensions.viewOnFirefox')} <ExternalLink className="h-4 w-4" />
      </a>
    </Card>
  );
});
ExtensionCard.displayName = 'ExtensionCard';

const Extensions = () => {
  const { t } = useTranslation();
  const { data: extensionsData, isLoading } = useExtensions();
  const count = extensionsData?.length ?? 0;

  return (
    <section id="extensions" className="py-14 sm:py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <SectionEyebrow index="07" label={t('extensions.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('extensions.title')}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{t('extensions.description')}</p>
        </div>

        <div
          className={`grid gap-6 mx-auto ${
            count === 1 ? 'grid-cols-1 max-w-md' : 'grid-cols-1 sm:grid-cols-2 max-w-4xl'
          }`}
        >
          {isLoading
            ? Array.from({ length: 2 }).map((_, idx) => <ExtensionCardSkeleton key={idx} />)
            : extensionsData?.map((extension) => <ExtensionCard key={extension.id} extension={extension} />)}
        </div>
      </div>
    </section>
  );
};

export default Extensions;
